'use client';

import React from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';

interface TextareaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onChange'
> {
  label?: string;
  error?: string;
  helperText?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

export default function Textarea({
  label,
  error,
  helperText,
  id,
  required,
  disabled,
  className = '',
  value,
  onChange,
  rows = 4,
  ...props
}: TextareaProps) {
  const { darkMode } = useDarkMode();

  // Generate a unique ID if not provided (using React.useId for SSR compatibility)
  const generatedId = React.useId();
  const textareaId = id || generatedId;

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={textareaId}
          className={`mb-2 block text-[13px] font-medium ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <textarea
        id={textareaId}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        rows={rows}
        className={`block w-full resize-none rounded-md px-3 py-1.5 text-base outline-1 -outline-offset-1 transition-all sm:text-sm/6 ${
          darkMode
            ? 'bg-white/5 text-gray-100 outline-white/10 placeholder:text-gray-500'
            : 'bg-white text-gray-900 outline-gray-300 placeholder:text-gray-400'
        } ${
          darkMode
            ? 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500'
            : 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600'
        } ${
          error
            ? darkMode
              ? 'outline-red-500/50 focus:outline-red-500'
              : 'outline-red-500 focus:outline-red-500'
            : ''
        } ${disabled ? 'disabled:cursor-not-allowed disabled:opacity-60' : ''} `}
        {...props}
      />

      {(error || helperText) && (
        <p
          className={`mt-2 text-sm ${
            error ? 'text-red-500' : darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
}
